<template>
    <AppModal :name="name" :beforeOpen="beforeOpen" :beforeClose="beforeClose" :max-width="600">

        <template v-slot:header>
            Add a Document
        </template>

        <template v-slot:content>
            <div class="modal-body p-0 pt-2 pb-4">
                <div class="row text-center">
                    <div class="col">
                        <a @click.prevent="selectType('UPLOAD')" href="#" class="uploadOption"
                           :class="getActiveClass('UPLOAD')">
                            <div class="font-22"><i class="fal fa-cloud-upload"></i></div>
                            Upload a Document
                        </a>
                    </div>
                    <div v-if="isSales || isPurchases" class="col">
                        <a @click.prevent="selectType('EMAIL')" href="#" class="uploadOption"
                           :class="getActiveClass('EMAIL')">
                            <div class="font-22"><i class="fal fa-at"></i></div>
                            Email {{ section.uriSingular }}
                        </a>
                    </div>
                    <div class="col">
                        <a @click.prevent="createDocumentWithoutAttachments" href="#" class="uploadOption"
                           :class="getActiveClass('CREATE')">
                            <div v-show="isCreatingInvoiceWithoutAttachments" class="font-22">
                                <i class="fas fa-spinner fa-pulse"></i>
                            </div>
                            <div v-show="!isCreatingInvoiceWithoutAttachments" class="font-22">
                                <i class="far fa-plus"></i>
                            </div>
                            Create a Document
                        </a>
                    </div>
                </div>
            </div>

            <div v-if="selected === 'UPLOAD'">
                <DocUploadContainer
                    :docTypeKey="docTypeKey"
                    :docStatusKey="status ? status.key : null"
                    :docSubmoduleKey="documentGroupSelected"
                    :locationUuid="location ? location.uuid : null"
                    :subsidiaryUuid="subsidiary ? subsidiary.uuid : null"
                    :onSuccessUpload="onSuccessUpload">
                    <template #upload-area="{ browse }">
                        <h5>
                            Drag and drop or <br/>
                            <button @click="browse" class="btn btn-primary">
                                Click here to upload
                            </button>
                        </h5>
                    </template>

                    <template #upload-info="{ files, filesUploadInfo }">
                        <div v-if="Object.keys(files).length !== 0" class="mt-3">
                            <DocUploadProgress
                                v-for="(file, fileId) in files" :key="fileId"
                                :file="file"
                                :info="filesUploadInfo[fileId] || {}"
                                class="mt-1"/>
                        </div>
                    </template>
                </DocUploadContainer>

                <div @click="expandAdvancedUploadSettings = !expandAdvancedUploadSettings"
                     class="text-left clickable text-muted font-16 mt-3">
                    Show advanced
                    <AppExpandChevron :show="expandAdvancedUploadSettings"/>
                </div>
                <div v-if="expandAdvancedUploadSettings" class="row mt-2 text-left">
                    <div v-if="getSubsidiaries.length > 1" class="col-6 mb-3">
                        <label>
                            {{ $lang.subsidiary.singular }}
                        </label>
                        <multiselect
                            v-model="subsidiary"
                            :options="getSubsidiaries"
                            :placeholder="`Select a ${$lang.subsidiary.singular}`"
                            track-by="uuid"
                            label="name"
                            :maxHeight="160">
                        </multiselect>
                    </div>
                    <div v-if="getLocations.length > 1" class="col-6 mb-3">
                        <label>
                            {{ $lang.location.singular }}
                        </label>
                        <multiselect
                            v-model="location"
                            :options="getLocations"
                            :placeholder="`Select a ${$lang.location.singular}`"
                            track-by="uuid"
                            label="name"
                            :maxHeight="160"/>
                    </div>
                    <div class="col-6 mb-3">
                        <label>
                            Ready Status
                        </label>
                        <AppSetStatus
                            class="d-block clickable"
                            :uuid="_.get(status, 'uuid')"
                            :statuses="dtStatuses.filter(s => s.isEnabled)"
                            @change="setStatus"/>
                    </div>
                </div>
            </div>

            <div v-else-if="selected === 'EMAIL'">
                <p class="font-18">Email us an {{ section.uriSingular }}</p>
                <p>Email {{ section.uri }} to your team inbox to add them to {{ appDisplayName }} .</p>

                <div v-if="isInboxListLoading" class="py-3">
                    Loading...
                </div>
                <div v-else-if="!isInboxListLoading && inboxList.length === 0" class="py-3">
                    No emails
                    <div @click="setupInbox"
                         class="clickable text-purple mt-3">
                        Setup <i class="fal fa-cog"></i>
                    </div>
                </div>
                <template v-else>
                    <div v-for="inbox in inboxList" :key="inbox.uuid" class="input-group mt-2">
                        <input type="email"
                               :value="inbox.email"
                               readonly
                               class="form-control form-control-sm bg-white">
                        <div class="input-group-append">
                            <button class="btn btn-sm btn-outline-primary mr-1"
                                    style="width: 40px;"
                                    @click="copyToClipboard(inbox.email)"
                                    v-tooltip="'Click to copy'">
                                <i class="fas fa-copy fa-lg"></i>
                            </button>
                        </div>
                    </div>
                </template>
            </div>

            <div class="modal-footer">
                <a @click.prevent="closeModal" href="#" class="font-16 mt-4">
                    Close
                </a>
            </div>
        </template>
    </AppModal>
</template>

<script>
import * as clipboard from 'clipboard-polyfill'
import {mapActions, mapGetters} from 'vuex'
import {SETTINGS_INBOXES} from '@/views/settings/settings-tabs'
import DocUploadContainer from '../../../../modals/documents/upload/DocUploadContainer'
import DocUploadProgress from '../../../../modals/documents/upload/DocUploadProgress'
import AppExpandChevron from '../../../../components/shared/AppExpandChevron'
import AppSetStatus from '../../../../components/setters/AppSetStatus'
import {INBOX_FETCH_LIST} from '@/store/modules/inbox'
import {openDocumentViewer} from '../view'
import {AppDocSystemTypes, getDocSystemTypeDefaultModel} from '@/types/document/types'
import {documentApi} from '@/api/document'
import {showErrorMessage} from '@/services/snackbar'

export default {
    name: 'CreateDocument',
    components: {AppSetStatus, AppExpandChevron, DocUploadProgress, DocUploadContainer},
    props: {
        name: {
            type: String,
            required: true
        }
    },
    data() {
        return {
            documentType: null,
            status: null,
            location: null,
            subsidiary: null,
            selected: 'UPLOAD',
            uploadWithError: 0,
            uploadDuplicates: 0,
            onSuccessUpload: null,
            expandAdvancedUploadSettings: false,

            inboxList: [],
            isInboxListLoading: false,
            isCreatingInvoiceWithoutAttachments: false
        }
    },
    computed: {
        ...mapGetters([
            'activeTeam',
            'getLocations',
            'getSubsidiaries'
        ]),

        ...mapGetters('documents', [
            'dtStatuses',
            'documentGroupSelected'
        ]),

        docTypeKey() {
            return this.documentType ? this.documentType.type : null
        },

        section() {
            return getDocSystemTypeDefaultModel(this.docTypeKey)
        },

        isSales() {
            return this.section.key === AppDocSystemTypes.SALES
        },

        isPurchases() {
            return this.section.key === AppDocSystemTypes.PURCHASING
        },

        isBatches() {
            return this.section.key === AppDocSystemTypes.BATCHES
        }
    },
    methods: {
        ...mapActions('inbox', {
            fetchInboxes: INBOX_FETCH_LIST
        }),

        beforeOpen({params}) {
            if (!params || !params.documentType || !params.documentType.uuid) {
                return
            }
            this.documentType = params.documentType
            this.onSuccessUpload = params.onSuccessUpload
            this.location = this.getLocations.find(l => l.uuid === params.locationId)
            this.subsidiary = this.getSubsidiaries.find(l => l.uuid === params.subsidiaryId)

            this.expandAdvancedUploadSettings = (!!this.location && this.getLocations.length > 1) ||
                (!!this.subsidiary && this.getSubsidiaries.length > 1)
        },

        beforeClose() {
            Object.assign(this.$data, this.$options.data.apply(this))
        },

        async createDocumentWithoutAttachments() {
            if (this.isCreatingInvoiceWithoutAttachments) {
                return
            }
            this.isCreatingInvoiceWithoutAttachments = true
            const {error, data} = await documentApi.createDocWithoutAttachments({
                type: this.docTypeKey,
                locationId: this.location ? this.location.uuid : null,
                subsidiaryId: this.subsidiary ? this.subsidiary.uuid : null
            })
            if (error) {
                showErrorMessage()
            } else {
                this.$modal.hide(this.name)
                await openDocumentViewer({id: data.uuid})
            }
            this.isCreatingInvoiceWithoutAttachments = false
        },

        getActiveClass(type) {
            return this.selected === type ? 'active' : ''
        },

        selectType(type) {
            this.selected = type
        },

        closeModal() {
            this.$modal.hide(this.name)
        },

        async fetchInboxList() {
            this.isInboxListLoading = true

            const list = await this.fetchInboxes()

            this.inboxList = list.filter(el => el.section === this.section.key)

            this.isInboxListLoading = false
        },

        setupInbox() {
            this.$router.push({name: 'settings', params: {page: SETTINGS_INBOXES}})
            this.closeModal()
        },

        copyToClipboard(email) {
            clipboard.writeText(email)
            this.$snackbar.show({type: 'SUCCESS', text: 'Email copied to clipboard'})
        },

        setStatus(status) {
            this.status = this.status === status ? null : status
        }
    },
    watch: {
        selected(v) {
            if (v === 'EMAIL' && this.inboxList.length === 0 && (this.isSales || this.isPurchases)) {
                this.fetchInboxList()
            }
        }
    }
}

</script>
<style>
.uploadOption {
    display: block;
    border-radius: 0px;
    padding: 15px 16px;
}

a.uploadOption {
    text-decoration: none;
}

a.uploadOption:hover {
    background: #f7f7f7;
}

.uploadOption.active {
    border-bottom: 3px solid;
}
</style>
